import React, { useState, useEffect } from 'react';
import {
  Card,
  Table,
  Button,
  Select,
  Row,
  Col,
  message,
  DatePicker,
  Tag,
  Spin,
  Checkbox,
  Icon
} from 'antd';
import moment from 'moment';
import { apiGet, apiPost } from './utils/apiService';

const { RangePicker } = DatePicker;
const { Option } = Select;

// Utility function to convert table data to CSV
const convertTableToCSV = (columns, dataSource, filename) => {
  // Extract headers from columns (exclude Action column)
  const headers = columns
    .filter(col => col.key !== 'action')
    .map(col => col.title || col.key || '')
    .filter(h => h);
  
  // Convert data rows
  const rows = dataSource.map((row, rowIndex) => {
    return columns
      .filter(col => col.key !== 'action')
      .map((col, colIndex) => {
        if (!col.dataIndex && !col.key && !col.render) return '';
        
        let value = '';
        
        // If there's a render function, try to extract the text value
        if (col.render && typeof col.render === 'function') {
          try {
            const dataIndex = col.dataIndex || col.key;
            const rawValue = dataIndex ? row[dataIndex] : row;
            const rendered = col.render(rawValue, row, rowIndex);
            
            // Extract text from rendered React elements or use raw value
            if (typeof rendered === 'string' || typeof rendered === 'number') {
              value = rendered;
            } else if (rendered && rendered.props && rendered.props.children) {
              // Try to extract text from React element (e.g., Tag components)
              const children = rendered.props.children;
              if (typeof children === 'string' || typeof children === 'number') {
                value = children;
              } else if (Array.isArray(children)) {
                value = children.map(c => {
                  if (typeof c === 'string' || typeof c === 'number') {
                    return c;
                  } else if (c && c.props && c.props.children) {
                    const nestedChildren = c.props.children;
                    if (typeof nestedChildren === 'string' || typeof nestedChildren === 'number') {
                      return nestedChildren;
                    } else if (Array.isArray(nestedChildren)) {
                      return nestedChildren.map(nc => typeof nc === 'string' || typeof nc === 'number' ? nc : '').join(' ');
                    }
                  }
                  return '';
                }).filter(v => v).join(' ');
              } else {
                value = rawValue !== null && rawValue !== undefined ? String(rawValue) : '';
              }
            } else {
              // Fallback to raw value
              const dataIndex = col.dataIndex || col.key;
              value = dataIndex ? row[dataIndex] : '';
            }
          } catch (e) {
            // If render fails, use raw value
            const dataIndex = col.dataIndex || col.key;
            value = dataIndex ? (row[dataIndex] !== null && row[dataIndex] !== undefined ? row[dataIndex] : '') : '';
          }
        } else {
          // No render function, use raw value
          const dataIndex = col.dataIndex || col.key;
          value = dataIndex ? row[dataIndex] : '';
        }
        
        // Format the value
        if (value === null || value === undefined || value === '') {
          value = '';
        } else if (typeof value === 'number') {
          value = value.toFixed(2);
        } else if (moment.isMoment(value)) {
          value = value.format('YYYY-MM-DD HH:mm:ss');
        } else if (typeof value === 'object') {
          value = JSON.stringify(value);
        } else {
          value = String(value);
        }
        
        // Handle date fields
        if (col.dataIndex && (col.dataIndex.includes('Date') || col.dataIndex.includes('date')) && value && !moment.isMoment(value)) {
          try {
            value = moment(value).format('YYYY-MM-DD HH:mm:ss');
          } catch (e) {
            // Keep original value if parsing fails
          }
        }
        
        // Escape CSV special characters
        if (String(value).includes(',') || String(value).includes('"') || String(value).includes('\n')) {
          value = `"${String(value).replace(/"/g, '""')}"`;
        }
        
        return value;
      });
  });
  
  // Combine headers and rows
  const csvContent = [
    headers.join(','),
    ...rows.map(row => row.join(','))
  ].join('\n');
  
  // Create blob and download
  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  const link = document.createElement('a');
  const url = URL.createObjectURL(blob);
  
  link.setAttribute('href', url);
  link.setAttribute('download', `${filename || 'full_tank_mileage_report'}_${moment().format('YYYY-MM-DD_HH-mm-ss')}.csv`);
  link.style.visibility = 'hidden';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

// Helper function to extract calculated reading from mileage
// Prefers calculatedReading from meterReadingRecord (cumulative, always increasing)
// Falls back to reading (physical meter reading, may reset on meter change)
const getCalculatedReading = (mileage) => {
  if (!mileage) return null;
  // Check for calculatedReading in meterReadingRecord first
  if (mileage.meterReadingRecord && 
      mileage.meterReadingRecord.calculatedReading !== null && 
      mileage.meterReadingRecord.calculatedReading !== undefined) {
    return mileage.meterReadingRecord.calculatedReading;
  }
  // Fall back to regular reading
  return mileage.reading;
};

// Helper function to get both readings for display purposes
const getMeterReadings = (mileage) => {
  if (!mileage) return { reading: null, calculatedReading: null };
  const reading = mileage.reading;
  const calculatedReading = mileage.meterReadingRecord?.calculatedReading ?? null;
  return { reading, calculatedReading };
};

function FullTankMileageReport() {
  const [loading, setLoading] = useState(false);
  const [dateRange, setDateRange] = useState(null);
  const [selectedAssetCode, setSelectedAssetCode] = useState(null);
  const [selectedAssetType, setSelectedAssetType] = useState(null);
  const [selectedAssetSubType, setSelectedAssetSubType] = useState(null);
  const [selectedAssetModel, setSelectedAssetModel] = useState(null);
  const [selectedLocation, setSelectedLocation] = useState(null);
  const [selectedSubLocation, setSelectedSubLocation] = useState(null);
  const [filterWithinThreshold, setFilterWithinThreshold] = useState(false);
  
  // Options for filters
  const [assetTypes, setAssetTypes] = useState([]);
  const [assetSubTypes, setAssetSubTypes] = useState([]);
  const [assetModels, setAssetModels] = useState([]);
  const [locations, setLocations] = useState([]);
  const [subLocations, setSubLocations] = useState([]);
  const [assetSearchOptions, setAssetSearchOptions] = useState([]);
  const [assetCodeMap, setAssetCodeMap] = useState({});
  
  // Data
  const [allReportData, setAllReportData] = useState([]); // Store all fetched data
  const [reportData, setReportData] = useState([]); // Filtered data for display
  const [standardRatesMap, setStandardRatesMap] = useState({}); // Map by model ID
  const [standardRatesByNameMap, setStandardRatesByNameMap] = useState({}); // Map by model name (normalized)
  const [varianceThresholdsMap, setVarianceThresholdsMap] = useState({}); // Map by model ID
  const [varianceThresholdsByNameMap, setVarianceThresholdsByNameMap] = useState({}); // Map by model name (normalized)

  // Load filter options on mount
  useEffect(() => {
    loadFilterOptions();
  }, []);

  // Reactively apply filters when filter values or allReportData changes
  useEffect(() => {
    if (allReportData.length === 0) return;
    
    let filtered = [...allReportData];
    
    // Filter by selected asset code from autocomplete
    if (selectedAssetCode) {
      filtered = filtered.filter(item => item.assetCode === selectedAssetCode);
    }
    
    if (selectedAssetType) {
      filtered = filtered.filter(item => item.assetType === selectedAssetType);
    }
    if (selectedAssetSubType) {
      filtered = filtered.filter(item => item.assetSubType === selectedAssetSubType);
    }
    if (selectedAssetModel) {
      filtered = filtered.filter(item => item.assetModel === selectedAssetModel);
    }
    if (selectedLocation) {
      filtered = filtered.filter(item => item.location === selectedLocation);
    }
    if (selectedSubLocation) {
      filtered = filtered.filter(item => item.subLocation === selectedSubLocation);
    }
    
    // Filter by variance threshold if checkbox is checked
    // When checked, filter OUT entries within threshold (show only entries outside threshold)
    if (filterWithinThreshold) {
      filtered = filtered.filter(item => {
        if (item.variancePercent === '-' || item.variancePercent === null || item.variancePercent === undefined) {
          return true; // Keep entries without variance percentage (can't determine if within threshold)
        }
        
        const variancePercent = parseFloat(item.variancePercent);
        if (isNaN(variancePercent)) return true; // Keep entries with invalid variance percentage
        
        // Get threshold for this asset's model
        const threshold = item.varianceThreshold;
        if (threshold === null || threshold === undefined || threshold === '-') {
          return true; // Keep entries without threshold (can't determine if within threshold)
        }
        
        const thresholdValue = parseFloat(threshold);
        if (isNaN(thresholdValue) || thresholdValue <= 0) {
          return true; // Keep entries with invalid threshold
        }
        
        // Filter OUT entries within threshold (show only entries where variance is outside threshold)
        return Math.abs(variancePercent) > thresholdValue;
      });
    }
    
    setReportData(filtered);
  }, [selectedAssetCode, selectedAssetType, selectedAssetSubType, selectedAssetModel, selectedLocation, selectedSubLocation, filterWithinThreshold, allReportData]);

  const loadFilterOptions = async () => {
    try {
      // Note: Filter options will be populated from the fetched fuel entries data
      // This is called on mount, but options will be updated after data is fetched
      // For now, initialize with empty arrays - they'll be populated after report generation
      setAssetTypes([]);
      setAssetSubTypes([]);
      setAssetModels([]);
      setLocations([]);
      setSubLocations([]);
      setAssetSearchOptions([]);
      setAssetCodeMap({});

      // Load asset models to get standard consumption rates and variance thresholds
      const modelsResult = await apiGet('/asset-model');
      if (modelsResult.success) {
        const models = modelsResult.data || modelsResult.models || (Array.isArray(modelsResult.data) ? modelsResult.data : []);
        const ratesMapById = {}; // Map by model ID
        const ratesMapByName = {}; // Map by model name (normalized)
        const thresholdsMapById = {}; // Map by model ID
        const thresholdsMapByName = {}; // Map by model name (normalized)
        
        models.forEach(model => {
          if (model._id && model.standardFuelConsumptionRate && Array.isArray(model.standardFuelConsumptionRate) && model.standardFuelConsumptionRate.length > 0) {
            const firstRate = model.standardFuelConsumptionRate[0];
            if (firstRate.fuelQty && firstRate.perType) {
              const rateData = {
                rate: firstRate.fuelQty,
                unit: firstRate.perType.name || '',
                modelId: model._id,
                modelName: model.name || ''
              };
              
              // Store by ID
              ratesMapById[model._id] = rateData;
              
              // Store by name (normalized)
              if (model.name) {
                const modelName = (model.name || '').trim().toLowerCase();
                ratesMapByName[modelName] = rateData;
                // Also store with original case for exact match
                ratesMapByName[model.name] = rateData;
              }
            }
          }
          
          // Load variance thresholds
          if (model._id && model.fuelMileageThreshold && Array.isArray(model.fuelMileageThreshold) && model.fuelMileageThreshold.length > 0) {
            const firstThreshold = model.fuelMileageThreshold[0];
            if (firstThreshold.value !== null && firstThreshold.value !== undefined && !isNaN(firstThreshold.value) && firstThreshold.value > 0) {
              const thresholdData = {
                threshold: firstThreshold.value,
                modelId: model._id,
                modelName: model.name || ''
              };
              
              // Store by ID
              thresholdsMapById[model._id] = thresholdData;
              
              // Store by name (normalized)
              if (model.name) {
                const modelName = (model.name || '').trim().toLowerCase();
                thresholdsMapByName[modelName] = thresholdData;
                // Also store with original case for exact match
                thresholdsMapByName[model.name] = thresholdData;
              }
            }
          }
        });
        
        console.log(`Loaded ${Object.keys(ratesMapById).length} asset models with standard rates`);
        console.log(`Loaded ${Object.keys(thresholdsMapById).length} asset models with variance thresholds`);
        setStandardRatesMap(ratesMapById);
        setStandardRatesByNameMap(ratesMapByName);
        setVarianceThresholdsMap(thresholdsMapById);
        setVarianceThresholdsByNameMap(thresholdsMapByName);
      }
    } catch (error) {
      console.error('Error loading filter options:', error);
    }
  };

  const generateReport = async () => {
    if (!dateRange || !dateRange[0] || !dateRange[1]) {
      message.warning('Please select a date range');
      return;
    }

    setLoading(true);
    setReportData([]);

    try {
      // Load asset models with standard rates and variance thresholds first
      let ratesMapById = {};
      let ratesMapByName = {};
      let thresholdsMapById = {};
      let thresholdsMapByName = {};
      
      try {
        const modelsResult = await apiGet('/asset-model', {
          headers: {
            'Accept': '*/*',
          },
        });
        
        if (!modelsResult.success) {
          console.error(`Failed to load asset models: ${modelsResult.error}`);
        } else {
          // Handle different response structures (same as IoTSummaryReport)
          let modelsData = [];
          if (Array.isArray(modelsResult.data)) {
            modelsData = modelsResult.data;
          } else if (modelsResult.data && Array.isArray(modelsResult.data.data)) {
            modelsData = modelsResult.data.data;
          } else if (modelsResult.data && Array.isArray(modelsResult.data.models)) {
            modelsData = modelsResult.data.models;
          } else if (modelsResult.data && typeof modelsResult.data === 'object') {
            // Try to find an array property
            for (const key of Object.keys(modelsResult.data)) {
              if (Array.isArray(modelsResult.data[key])) {
                modelsData = modelsResult.data[key];
                break;
              }
            }
          }
          
          console.log(`Extracted ${modelsData.length} models from API response`);
          
          modelsData.forEach(model => {
            if (!model._id) return;
            
            const modelName = (model.name || '').trim();
            const normalizedName = modelName.toLowerCase();
            
            if (model.standardFuelConsumptionRate && Array.isArray(model.standardFuelConsumptionRate) && model.standardFuelConsumptionRate.length > 0) {
              // Process all rates in the array - store all rates with unit type as key
              const modelRates = [];
              model.standardFuelConsumptionRate.forEach(rateObj => {
                const fuelQty = rateObj.fuelQty;
                
                if (fuelQty === null || fuelQty === undefined || isNaN(fuelQty) || fuelQty <= 0) {
                  return; // Skip invalid rates
                }
                
                if (rateObj.perType && rateObj.perType.name) {
                  const unitName = rateObj.perType.name.toUpperCase();
                  let rate = null;
                  let unit = '';
                  
                  // API format might be "L/KM" or "L/HOURS" (litres per unit)
                  // We need to convert to display format
                  if (unitName === 'KMS' || unitName === 'KM' || unitName.includes('KM')) {
                    // Convert L/KM to KM/L: if API says 0.1 L/KM, that means 10 KM/L
                    rate = 1 / fuelQty;
                    unit = 'KM/L';
                  } else if (unitName === 'HOURS' || unitName === 'HOUR' || unitName === 'HRS' || unitName.includes('HOUR')) {
                    // L/HOURS stays the same
                    rate = fuelQty;
                    unit = 'L/HOURS';
                  } else if (unitName === 'KWH' || unitName.includes('KWH')) {
                    // Convert L/KWH to KWH/L: if API says 0.1 L/KWH, that means 10 KWH/L
                    rate = 1 / fuelQty;
                    unit = 'KWH/L';
                  }
                  
                  if (rate !== null) {
                    const rateData = {
                      rate: rate,
                      unit: unit,
                      modelId: model._id,
                      modelName: modelName,
                      originalFuelQty: fuelQty,
                      originalUnit: rateObj.perType.name,
                      unitType: unitName // Store unit type for matching
                    };
                    
                    modelRates.push(rateData);
                    
                    // Store by ID with unit as key (to support multiple rates per model)
                    const keyById = `${model._id}_${unit}`;
                    if (!ratesMapById[keyById]) {
                      ratesMapById[keyById] = rateData;
                    }
                    
                    // Also store first rate by ID for backward compatibility
                    if (!ratesMapById[model._id]) {
                      ratesMapById[model._id] = rateData;
                    }
                    
                    // Store by name (normalized) with unit as key
                    if (modelName) {
                      const keyByName = `${normalizedName}_${unit}`;
                      if (!ratesMapByName[keyByName]) {
                        ratesMapByName[keyByName] = rateData;
                      }
                      // Also store with original case
                      const keyByNameOriginal = `${modelName}_${unit}`;
                      if (!ratesMapByName[keyByNameOriginal]) {
                        ratesMapByName[keyByNameOriginal] = rateData;
                      }
                      // Store first rate by name for backward compatibility
                      if (!ratesMapByName[normalizedName]) {
                        ratesMapByName[normalizedName] = rateData;
                      }
                      if (!ratesMapByName[modelName]) {
                        ratesMapByName[modelName] = rateData;
                      }
                    }
                  }
                }
              });
            }
            
            // Load variance thresholds
            if (model.fuelMileageThreshold && Array.isArray(model.fuelMileageThreshold) && model.fuelMileageThreshold.length > 0) {
              const firstThreshold = model.fuelMileageThreshold[0];
              if (firstThreshold.value !== null && firstThreshold.value !== undefined && !isNaN(firstThreshold.value) && firstThreshold.value > 0) {
                const thresholdData = {
                  threshold: firstThreshold.value,
                  modelId: model._id,
                  modelName: modelName
                };
                
                // Store by ID
                if (!thresholdsMapById[model._id]) {
                  thresholdsMapById[model._id] = thresholdData;
                }
                
                // Store by name (normalized)
                if (modelName) {
                  if (!thresholdsMapByName[normalizedName]) {
                    thresholdsMapByName[normalizedName] = thresholdData;
                  }
                  // Also store with original case for exact match
                  if (!thresholdsMapByName[modelName]) {
                    thresholdsMapByName[modelName] = thresholdData;
                  }
                }
              }
            }
          });
          
          console.log(`Loaded ${Object.keys(ratesMapById).length} asset models with standard rates for report`);
          console.log(`Loaded ${Object.keys(thresholdsMapById).length} asset models with variance thresholds for report`);
          console.log('Sample rates:', Object.keys(ratesMapById).slice(0, 3).map(id => ({
            id,
            name: ratesMapById[id].modelName,
            rate: ratesMapById[id].rate,
            unit: ratesMapById[id].unit
          })));
        }
      } catch (error) {
        console.error('Error loading asset models for report:', error);
      }

      const startTimestamp = dateRange[0].startOf('day').valueOf();
      const endTimestamp = dateRange[1].endOf('day').valueOf();
      const startISO = moment(startTimestamp).toISOString();
      const endISO = moment(endTimestamp).toISOString();

      // Fetch all fuel entries for all assets in one call (only date range filter at API level)
      console.log('Fetching fuel entries for all assets...');
      const allFuelEntriesResult = await apiPost('/fuel/mileage-report', {
        asset: null,
        location: 'all',
        subLocation: 'all',
        periodFrom: startISO,
        periodTo: endISO,
      });

      if (!allFuelEntriesResult.success) {
        message.error(`Failed to fetch fuel entries: ${allFuelEntriesResult.error}`);
        setLoading(false);
        return;
      }

      const allFuelEntries = allFuelEntriesResult.data || [];
      console.log(`Fetched ${allFuelEntries.length} fuel entries for all assets`);
      
      // Debug: Log sample entry to see structure
      if (allFuelEntries.length > 0) {
        console.log('Sample fuel entry structure:', {
          hasAsset: !!allFuelEntries[0].asset,
          assetType: typeof allFuelEntries[0].asset,
          assetStructure: allFuelEntries[0].asset ? {
            assetCode: allFuelEntries[0].asset.assetCode,
            assetType: allFuelEntries[0].asset.assetType,
            equipmentSubType: allFuelEntries[0].asset.equipmentSubType,
            assetModel: allFuelEntries[0].asset.assetModel,
            location: allFuelEntries[0].asset.location,
            subLocation: allFuelEntries[0].asset.subLocation
          } : null
        });
      }

      // Extract unique assets from fuel entries and build asset map
      const assetMap = {}; // Map by assetCode
      const fuelEntriesByAsset = {};
      const assetCandidates = {}; // Store all asset candidates to find best one with populated fields
      
      // First pass: collect all entries and find best asset data (with populated fields)
      allFuelEntries.forEach(entry => {
        // Extract asset information from entry
        // Note: assetCode is in entry.asset.assetCode, but populated objects are at root level
        let assetCode = null;
        
        if (entry.asset) {
          if (typeof entry.asset === 'object' && entry.asset.assetCode) {
            assetCode = entry.asset.assetCode;
          }
        }
        
        // Fallback to direct assetCode field
        if (!assetCode && entry.assetCode) {
          assetCode = entry.assetCode;
        }
        
        if (!assetCode) {
          return; // Skip entries without asset code
        }
        
        // Group entries by asset code
        if (!fuelEntriesByAsset[assetCode]) {
          fuelEntriesByAsset[assetCode] = [];
        }
        fuelEntriesByAsset[assetCode].push(entry);
        
        // Store the entire entry as candidate (populated objects are at root level, not in entry.asset)
        if (!assetCandidates[assetCode]) {
          assetCandidates[assetCode] = [];
        }
        assetCandidates[assetCode].push(entry);
      });

      // Helper function to extract name from object or return the value
      const extractName = (field) => {
        if (!field) return null;
        
        if (typeof field === 'string') {
          // Check if it looks like a MongoDB ObjectId (24 hex characters)
          // Also check for patterns like "6863a79de255a 176afefdddf" (two IDs with space)
          const isObjectId = /^[0-9a-fA-F]{24}$/i.test(field.trim());
          const isObjectIdPattern = /^[0-9a-fA-F]{12,24}\s+[0-9a-fA-F]{12,24}$/i.test(field.trim());
          
          if (isObjectId || isObjectIdPattern) {
            return null; // It's an ID, not a name - don't use it
          }
          // If it's a short string that doesn't look like an ID, it might be a code/name
          // But if it's long and looks like an ID pattern, skip it
          if (field.length > 20 && /^[0-9a-fA-F\s]+$/i.test(field)) {
            return null; // Looks like an ID pattern
          }
          return field; // It's likely a name string
        }
        
        if (typeof field === 'object') {
          // Try multiple possible name fields, but NEVER return _id
          return field.name || field.code || field.title || field.label || null;
        }
        
        return null;
      };

      // Second pass: build asset map from best candidates (prefer entries with populated fields)
      // Note: Populated objects (assetModel, assetSubType, assetType, location, subLocation) are at ROOT level of entry
      Object.keys(assetCandidates).forEach(assetCode => {
        const candidates = assetCandidates[assetCode];
        
        // Find the best candidate (one with most populated fields)
        let bestEntry = candidates[0];
        let bestScore = 0;
        
        candidates.forEach(entry => {
          let score = 0;
          // Check root level populated objects (not entry.asset)
          if (entry.assetModel && typeof entry.assetModel === 'object' && entry.assetModel.name) score += 10;
          if (entry.assetSubType && typeof entry.assetSubType === 'object' && entry.assetSubType.name) score += 10;
          if (entry.location && typeof entry.location === 'object' && entry.location.name) score += 5;
          if (entry.subLocation && typeof entry.subLocation === 'object' && entry.subLocation.name) score += 5;
          if (entry.assetType && typeof entry.assetType === 'object' && entry.assetType.name) score += 5;
          
          if (score > bestScore) {
            bestScore = score;
            bestEntry = entry;
          }
        });
        
        const entry = bestEntry;

        // Extract from root level of entry (populated objects are here, not in entry.asset)
        // Extract asset type (at root level)
        const assetType = extractName(entry.assetType);

        // Extract asset sub type (at root level)
        const assetSubType = extractName(entry.assetSubType);

        // Extract asset model (at root level)
        const assetModel = extractName(entry.assetModel);

        // Extract location (at root level)
        const location = extractName(entry.location);

        // Extract sub location (at root level)
        const subLocation = extractName(entry.subLocation);

        // Debug: Log first asset to see structure
        if (Object.keys(assetMap).length === 0) {
          console.log('Sample entry structure:', {
            assetCode,
            hasAsset: !!entry.asset,
            assetCodeFromAsset: entry.asset?.assetCode,
            rootLevelObjects: {
              assetType: entry.assetType,
              assetSubType: entry.assetSubType,
              assetModel: entry.assetModel,
              location: entry.location,
              subLocation: entry.subLocation
            },
            extracted: {
              assetType,
              assetSubType,
              assetModel,
              location,
              subLocation
            },
            bestScore
          });
        }

        assetMap[assetCode] = {
          assetCode: assetCode,
          _id: entry.asset?._id,
          ownerCode: entry.asset?.ownerCode || entry.asset?.ownercode || '',
          altName: entry.asset?.altName || entry.asset?.altname || '',
          assetType: assetType,
          assetSubType: assetSubType,
          assetModel: assetModel,
          location: location,
          subLocation: subLocation,
          // Store original objects for filtering
          _assetTypeObj: entry.assetType,
          _assetSubTypeObj: entry.assetSubType,
          _assetModelObj: entry.assetModel,
          _locationObj: entry.location,
          _subLocationObj: entry.subLocation
        };
      });

      console.log(`Extracted ${Object.keys(assetMap).length} unique assets from fuel entries`);
      console.log(`Grouped fuel entries for ${Object.keys(fuelEntriesByAsset).length} assets`);

      // Populate filter options from extracted asset data
      const types = [...new Set(Object.values(assetMap).map(a => a.assetType).filter(Boolean))].sort();
      const subTypes = [...new Set(Object.values(assetMap).map(a => a.assetSubType).filter(Boolean))].sort();
      const models = [...new Set(Object.values(assetMap).map(a => a.assetModel).filter(Boolean))].sort();
      const locs = [...new Set(Object.values(assetMap).map(a => a.location).filter(Boolean))].sort();
      const subLocs = [...new Set(Object.values(assetMap).map(a => a.subLocation).filter(Boolean))].sort();
      const assetOptions = Object.values(assetMap)
        .sort((a, b) => a.assetCode.localeCompare(b.assetCode))
        .map(asset => ({
          value: asset.assetCode,
          label: `${asset.assetCode}${asset.ownerCode ? ` (${asset.ownerCode})` : ''}${asset.altName ? ` - ${asset.altName}` : ''}`
        }));
      
      setAssetTypes(types);
      setAssetSubTypes(subTypes);
      setAssetModels(models);
      setLocations(locs);
      setSubLocations(subLocs);
      setAssetSearchOptions(assetOptions);
      setAssetCodeMap(assetMap);

      // Helper function to match filter value
      // Now we match against both the name (display value) and the original object (for ID matching)
      const matchesFilter = (assetFieldName, assetFieldObj, filterValue) => {
        if (!filterValue) return true;
        if (!assetFieldName && !assetFieldObj) return false;
        
        // Match by name (display value)
        if (assetFieldName && assetFieldName === filterValue) {
          return true;
        }
        
        // Match by ID from original object
        if (assetFieldObj) {
          if (typeof assetFieldObj === 'string') {
            return assetFieldObj === filterValue;
          }
          if (typeof assetFieldObj === 'object') {
            if (assetFieldObj._id === filterValue || assetFieldObj.name === filterValue || assetFieldObj.code === filterValue) {
              return true;
            }
          }
        }
        
        return String(assetFieldName || assetFieldObj) === String(filterValue);
      };

      // Apply filters to assets extracted from fuel entries
      let filteredAssetCodes = Object.keys(assetMap);
      
      if (selectedAssetType) {
        filteredAssetCodes = filteredAssetCodes.filter(code => 
          matchesFilter(assetMap[code].assetType, assetMap[code]._assetTypeObj, selectedAssetType)
        );
      }
      if (selectedAssetSubType) {
        filteredAssetCodes = filteredAssetCodes.filter(code => 
          matchesFilter(assetMap[code].assetSubType, assetMap[code]._assetSubTypeObj, selectedAssetSubType)
        );
      }
      if (selectedAssetModel) {
        filteredAssetCodes = filteredAssetCodes.filter(code => 
          matchesFilter(assetMap[code].assetModel, assetMap[code]._assetModelObj, selectedAssetModel)
        );
      }
      if (selectedLocation) {
        filteredAssetCodes = filteredAssetCodes.filter(code => 
          matchesFilter(assetMap[code].location, assetMap[code]._locationObj, selectedLocation)
        );
      }
      if (selectedSubLocation) {
        filteredAssetCodes = filteredAssetCodes.filter(code => 
          matchesFilter(assetMap[code].subLocation, assetMap[code]._subLocationObj, selectedSubLocation)
        );
      }

      console.log(`Processing ${filteredAssetCodes.length} assets with filters applied`);

      // Process each filtered asset
      const reportRows = [];
      
      for (const assetCode of filteredAssetCodes) {
        try {
          const asset = assetMap[assetCode];
          if (!asset) continue;

          // Get fuel entries for this asset
          const allFuelEntries = fuelEntriesByAsset[assetCode] || [];
          
          if (allFuelEntries.length === 0) {
            continue; // No fuel entries for this asset
          }

          // Sort entries by date
          allFuelEntries.sort((a, b) => {
            const dateA = moment(a.date || a.datetime);
            const dateB = moment(b.date || b.datetime);
            return dateA.valueOf() - dateB.valueOf();
          });

          // Get ALL full tank events from fetched data (including those outside date range)
          // The API fetches previous full tank events to enable calculation for first event in range
          const allFullTankEvents = allFuelEntries
            .filter(entry => entry.isFullTank === true)
            .sort((a, b) => {
              const dateA = moment(a.date || a.datetime);
              const dateB = moment(b.date || b.datetime);
              return dateA.valueOf() - dateB.valueOf();
            });

          if (allFullTankEvents.length < 2) {
            // Need at least 2 full tank events to calculate consumption rate
            continue;
          }

          // Filter for full tank events within the date range (for reporting)
          const fullTankEventsInRange = allFullTankEvents.filter(entry => {
            const entryDate = moment(entry.date || entry.datetime);
            return entryDate.isBetween(moment(startTimestamp), moment(endTimestamp), null, '[]');
          });

          if (fullTankEventsInRange.length === 0) {
            // No full tank events in the date range
            continue;
          }

          // Get asset model info for standard rate and variance threshold from preloaded maps
          let standardRate = null;
          let standardRateUnit = null;
          let varianceThreshold = null;
          let allKwhRate = null;
          let allKmRate = null;
          let allHoursRate = null;
          if (asset.assetModel) {
            // Get model ID or name
            const modelId = typeof asset.assetModel === 'object' ? asset.assetModel._id : asset.assetModel;
            const modelName = typeof asset.assetModel === 'object' ? asset.assetModel.name : (typeof asset.assetModel === 'string' ? asset.assetModel : null);
            
            // Try to find standard rate in preloaded maps
            // Priority: KWH > HOURS > KM (if multiple rates exist)
            let rateData = null;
            let allRatesForModel = [];
            
            // Collect all rates for this model
            if (modelId) {
              // Try to find all rates by ID pattern
              Object.keys(ratesMapById).forEach(key => {
                if (key.startsWith(`${modelId}_`)) {
                  allRatesForModel.push(ratesMapById[key]);
                } else if (key === modelId) {
                  allRatesForModel.push(ratesMapById[key]);
                }
              });
            }
            
            if (allRatesForModel.length === 0) {
              // Fallback: try by name
              if (modelName) {
                const normalizedName = modelName.trim().toLowerCase();
                Object.keys(ratesMapByName).forEach(key => {
                  if (key.startsWith(`${normalizedName}_`) || key.startsWith(`${modelName}_`)) {
                    allRatesForModel.push(ratesMapByName[key]);
                  } else if (key === normalizedName || key === modelName) {
                    allRatesForModel.push(ratesMapByName[key]);
                  }
                });
              }
            }
            
            // Priority order: KWH/L > KM/L > L/HOURS
            // Store individual rates for per-entry meter-aware re-selection
            if (allRatesForModel.length > 0) {
              allKwhRate = allRatesForModel.find(r => r.unit === 'KWH/L') || null;
              allKmRate = allRatesForModel.find(r => r.unit === 'KM/L') || null;
              allHoursRate = allRatesForModel.find(r => r.unit === 'L/HOURS') || null;

              // Default: KWH > KMS > HOURS (may be overridden per-entry based on available meters)
              rateData = allKwhRate || allKmRate || allHoursRate || allRatesForModel[0];
              
              if (rateData) {
                standardRate = rateData.rate;
                standardRateUnit = rateData.unit;
                console.log(`Found standard rate for ${assetCode} (model ID: ${modelId}, model: ${modelName}): ${rateData.rate} ${rateData.unit} (priority: ${allKwhRate ? 'KWH' : allKmRate ? 'KMS' : 'HOURS'})`);
              }
            } else {
              // Fallback to old method for backward compatibility
              if (modelId && ratesMapById[modelId]) {
                rateData = ratesMapById[modelId];
                standardRate = rateData.rate;
                standardRateUnit = rateData.unit;
                console.log(`Found standard rate by ID for ${assetCode} (model ID: ${modelId}): ${rateData.rate} ${rateData.unit}`);
              } else if (modelName && ratesMapByName[modelName]) {
                rateData = ratesMapByName[modelName];
                standardRate = rateData.rate;
                standardRateUnit = rateData.unit;
                console.log(`Found standard rate by exact name for ${assetCode} (model: ${modelName}): ${rateData.rate} ${rateData.unit}`);
              } else if (modelName) {
                const normalizedName = modelName.trim().toLowerCase();
                if (ratesMapByName[normalizedName]) {
                  rateData = ratesMapByName[normalizedName];
                  standardRate = rateData.rate;
                  standardRateUnit = rateData.unit;
                  console.log(`Found standard rate by normalized name for ${assetCode} (model: ${modelName}): ${rateData.rate} ${rateData.unit}`);
                }
              }
              
              if (!rateData) {
                console.log(`No standard rate found for asset ${assetCode}, model ID: ${modelId}, model name: ${modelName}`);
                console.log(`Available model IDs: ${Object.keys(ratesMapById).slice(0, 5).join(', ')}...`);
                console.log(`Available model names: ${Object.keys(ratesMapByName).slice(0, 5).join(', ')}...`);
              }
            }
            
            // Try to find variance threshold in preloaded maps
            let thresholdData = null;
            
            // First try by ID
            if (modelId && thresholdsMapById[modelId]) {
              thresholdData = thresholdsMapById[modelId];
            }
            // Then try by name (exact match)
            else if (modelName && thresholdsMapByName[modelName]) {
              thresholdData = thresholdsMapByName[modelName];
            }
            // Then try by normalized name
            else if (modelName) {
              const normalizedName = modelName.trim().toLowerCase();
              if (thresholdsMapByName[normalizedName]) {
                thresholdData = thresholdsMapByName[normalizedName];
              }
            }
            
            if (thresholdData) {
              varianceThreshold = thresholdData.threshold;
            }
          } else {
            console.log(`Asset ${assetCode} has no assetModel field`);
          }

          // Calculate consumption rate for each full tank event within the date range
          // Use allFullTankEvents to find previous event (even if outside date range)
          for (const currentEvent of fullTankEventsInRange) {
            const currentEventDate = moment(currentEvent.date || currentEvent.datetime);
            
            // Find the previous full tank event from ALL events (including those outside date range)
            // This allows calculation for the first full tank event in the range
            let previousEvent = null;
            for (let i = allFullTankEvents.length - 1; i >= 0; i--) {
              const event = allFullTankEvents[i];
              const eventDate = moment(event.date || event.datetime);
              if (eventDate.isBefore(currentEventDate)) {
                previousEvent = event;
                break;
              }
            }
            
            // Skip if no previous full tank event found
            if (!previousEvent) {
              continue;
            }

            const previousEventDate = moment(previousEvent.date || previousEvent.datetime);

            // Calculate fuel consumed: current full tank + all non-full tank events between previous and current
            const fuelConsumed = allFuelEntries
              .filter(entry => {
                const entryDate = moment(entry.date || entry.datetime);
                return (entryDate.isSame(currentEventDate) || 
                        (entryDate.isAfter(previousEventDate) && entryDate.isBefore(currentEventDate)));
              })
              .reduce((sum, entry) => sum + (entry.quantity || 0), 0);

            // Calculate meter run
            // Priority: Match standard rate unit if available, otherwise use meter type
            let meterRun = null;
            let meterName = null;
            let consumptionRate = null;
            let consumptionRateUnit = null;
            
            // Collect all available meters
            // Use calculatedReading for run calculation (cumulative, handles meter resets)
            const availableMeters = [];
            if (currentEvent.mileage && Array.isArray(currentEvent.mileage) && 
                previousEvent.mileage && Array.isArray(previousEvent.mileage)) {
              
              for (const currentMeter of currentEvent.mileage) {
                const meterNameUpper = (currentMeter.name || '').toUpperCase();
                const previousMeter = previousEvent.mileage.find(m => 
                  (m.name || '').toUpperCase() === meterNameUpper
                );

                // Use calculatedReading (cumulative) for run calculation
                const currentReading = getCalculatedReading(currentMeter);
                const previousReading = getCalculatedReading(previousMeter);

                if (currentReading !== null && previousReading !== null) {
                  const run = currentReading - previousReading;
                  
                  if (run > 0) {
                    // Store both readings for display
                    const currentReadings = getMeterReadings(currentMeter);
                    const prevReadings = getMeterReadings(previousMeter);
                    
                    availableMeters.push({
                      name: currentMeter.name,
                      nameUpper: meterNameUpper,
                      run: run,
                      currentReading: currentReadings.reading,
                      currentCalcReading: currentReadings.calculatedReading,
                      previousReading: prevReadings.reading,
                      previousCalcReading: prevReadings.calculatedReading,
                    });
                  }
                }
              }
            }
            
            // Re-select standard rate for THIS entry based on which meters are actually available
            // Priority still KWH > KMS > HOURS, but only among rates with matching meters
            let entryStandardRate = standardRate;
            let entryStandardRateUnit = standardRateUnit;
            if (allKwhRate || allKmRate || allHoursRate) {
              const hasKwh = availableMeters.some(m => m.nameUpper.includes('KWH'));
              const hasKm = availableMeters.some(m => m.nameUpper.includes('KM') || m.nameUpper.includes('ODO') || m.nameUpper.includes('DISTANCE'));
              const hasHours = availableMeters.some(m => m.nameUpper.includes('HOUR') || m.nameUpper.includes('HRS'));

              let entryRateData = null;
              if (allKwhRate && hasKwh) entryRateData = allKwhRate;
              else if (allKmRate && hasKm) entryRateData = allKmRate;
              else if (allHoursRate && hasHours) entryRateData = allHoursRate;

              if (entryRateData) {
                entryStandardRate = entryRateData.rate;
                entryStandardRateUnit = entryRateData.unit;
              }
            }

            // Select meter matching the entry-level standard rate unit
            let selectedMeter = null;
            if (entryStandardRateUnit && availableMeters.length > 0) {
              if (entryStandardRateUnit === 'KWH/L') {
                selectedMeter = availableMeters.find(m => m.nameUpper.includes('KWH'));
              } else if (entryStandardRateUnit === 'KM/L') {
                selectedMeter = availableMeters.find(m => m.nameUpper.includes('KM') || m.nameUpper.includes('ODO') || m.nameUpper.includes('DISTANCE'));
              } else if (entryStandardRateUnit === 'L/HOURS') {
                selectedMeter = availableMeters.find(m => m.nameUpper.includes('HOUR') || m.nameUpper.includes('HRS'));
              }
            } else if (availableMeters.length > 0) {
              // No standard rate, prioritize: KWH > KMS > HOURS
              selectedMeter = availableMeters.find(m => m.nameUpper.includes('KWH')) ||
                             availableMeters.find(m => m.nameUpper.includes('KM') || m.nameUpper.includes('ODO') || m.nameUpper.includes('DISTANCE')) ||
                             availableMeters.find(m => m.nameUpper.includes('HOUR') || m.nameUpper.includes('HRS')) ||
                             availableMeters[0];
            }

            // Calculate consumption rate — only when meter matches the standard rate unit
            if (selectedMeter && fuelConsumed > 0) {
              meterRun = selectedMeter.run;
              meterName = selectedMeter.name;
              const meterNameUpper = selectedMeter.nameUpper;

              if (entryStandardRateUnit) {
                consumptionRateUnit = entryStandardRateUnit;

                if (entryStandardRateUnit === 'KWH/L' && meterNameUpper.includes('KWH')) {
                  consumptionRate = meterRun / fuelConsumed;
                } else if (entryStandardRateUnit === 'L/HOURS' && (meterNameUpper.includes('HOUR') || meterNameUpper.includes('HRS'))) {
                  consumptionRate = fuelConsumed / meterRun;
                } else if (entryStandardRateUnit === 'KM/L' && (meterNameUpper.includes('KM') || meterNameUpper.includes('ODO') || meterNameUpper.includes('DISTANCE'))) {
                  consumptionRate = meterRun / fuelConsumed;
                } else {
                  consumptionRate = null;
                  consumptionRateUnit = null;
                }
              } else {
                // No standard rate, calculate based on meter type
                if (meterNameUpper.includes('KM') || meterNameUpper.includes('ODO') || meterNameUpper.includes('DISTANCE')) {
                  consumptionRate = meterRun / fuelConsumed;
                  consumptionRateUnit = 'KM/L';
                } else if (meterNameUpper.includes('HOUR') || meterNameUpper.includes('HRS')) {
                  consumptionRate = fuelConsumed / meterRun;
                  consumptionRateUnit = 'L/HOURS';
                } else if (meterNameUpper.includes('KWH')) {
                  consumptionRate = meterRun / fuelConsumed;
                  consumptionRateUnit = 'KWH/L';
                }
              }
            }

            // Calculate variance if we have standard rate
            let variance = null;
            let variancePercent = null;
            let fuelLoss = null;
            if (entryStandardRate && consumptionRate && entryStandardRateUnit && consumptionRateUnit) {
              if (consumptionRateUnit === entryStandardRateUnit) {
                if (consumptionRateUnit === 'KM/L' || consumptionRateUnit === 'KWH/L') {
                  variance = entryStandardRate - consumptionRate;
                  variancePercent = entryStandardRate > 0 ? ((variance / entryStandardRate) * 100) : null;
                  if (meterRun && entryStandardRate > 0) {
                    const expectedFuel = meterRun / entryStandardRate;
                    fuelLoss = fuelConsumed - expectedFuel;
                  }
                } else if (consumptionRateUnit === 'L/HOURS') {
                  variance = consumptionRate - entryStandardRate;
                  variancePercent = entryStandardRate > 0 ? ((variance / entryStandardRate) * 100) : null;
                  if (meterRun && entryStandardRate > 0) {
                    const expectedFuel = entryStandardRate * meterRun;
                    fuelLoss = fuelConsumed - expectedFuel;
                  }
                } else {
                  variance = consumptionRate - entryStandardRate;
                  variancePercent = entryStandardRate > 0 ? ((variance / entryStandardRate) * 100) : null;
                }
              } else {
                console.log(`Unit mismatch for ${assetCode}: consumption=${consumptionRateUnit}, standard=${entryStandardRateUnit}`);
              }
            }

            // Add row to report (use extracted names, show '-' if null/empty)
            reportRows.push({
              key: `${assetCode}_${currentEventDate.valueOf()}`,
              assetCode: assetCode,
              ownerCode: asset.ownerCode || '',
              altName: asset.altName || '',
              assetType: (asset.assetType && asset.assetType.trim()) || '-',
              assetSubType: (asset.assetSubType && asset.assetSubType.trim()) || '-',
              assetModel: (asset.assetModel && asset.assetModel.trim()) || '-',
              location: (asset.location && asset.location.trim()) || '-',
              subLocation: (asset.subLocation && asset.subLocation.trim()) || '-',
              previousFullTankDate: previousEventDate.format('YYYY-MM-DD HH:mm:ss'),
              currentFullTankDate: currentEventDate.format('YYYY-MM-DD HH:mm:ss'),
              fuelConsumed: fuelConsumed.toFixed(2),
              meterRun: meterRun ? meterRun.toFixed(2) : '-',
              meterName: meterName || '-',
              consumptionRate: consumptionRate ? consumptionRate.toFixed(2) : '-',
              consumptionRateUnit: consumptionRateUnit || '-',
              standardRate: entryStandardRate ? entryStandardRate.toFixed(2) : '-',
              standardRateUnit: entryStandardRateUnit || '-',
              variance: variance !== null ? variance.toFixed(2) : '-',
              variancePercent: variancePercent !== null ? variancePercent.toFixed(2) : '-',
              fuelLoss: fuelLoss !== null ? fuelLoss.toFixed(2) : '-',
              varianceThreshold: varianceThreshold !== null ? varianceThreshold.toFixed(2) : '-',
              voucherNo: currentEvent.voucherNo || '-'
            });
          }
        } catch (error) {
          console.error(`Error processing asset ${assetCode}:`, error);
        }
      }

      // Store all report data (filters will be applied reactively via useEffect)
      setAllReportData(reportRows);
      message.success(`Generated report with ${reportRows.length} full tank to full tank entries`);
    } catch (error) {
      console.error('Error generating report:', error);
      message.error('Failed to generate report');
    } finally {
      setLoading(false);
    }
  };

  const columns = [
    {
      title: 'Asset Code',
      dataIndex: 'assetCode',
      key: 'assetCode',
      fixed: 'left',
      width: 220,
      sorter: (a, b) => {
        const aVal = a.assetCode || '';
        const bVal = b.assetCode || '';
        return aVal.localeCompare(bVal);
      },
      render: (value, record) => {
        const parts = [value || '-'];
        if (record.ownerCode) {
          parts.push(`(${record.ownerCode})`);
        }
        if (record.altName) {
          parts.push(`- ${record.altName}`);
        }
        return parts.join(' ');
      }
    },
    {
      title: 'Asset Type',
      dataIndex: 'assetType',
      key: 'assetType',
      width: 120,
      sorter: (a, b) => {
        const aVal = a.assetType || '';
        const bVal = b.assetType || '';
        return aVal.localeCompare(bVal);
      }
    },
    {
      title: 'Asset Sub Type',
      dataIndex: 'assetSubType',
      key: 'assetSubType',
      width: 130,
      sorter: (a, b) => {
        const aVal = a.assetSubType || '';
        const bVal = b.assetSubType || '';
        return aVal.localeCompare(bVal);
      }
    },
    {
      title: 'Asset Model',
      dataIndex: 'assetModel',
      key: 'assetModel',
      width: 120,
      sorter: (a, b) => {
        const aVal = a.assetModel || '';
        const bVal = b.assetModel || '';
        return aVal.localeCompare(bVal);
      }
    },
    {
      title: 'Location',
      dataIndex: 'location',
      key: 'location',
      width: 120,
      sorter: (a, b) => {
        const aVal = a.location || '';
        const bVal = b.location || '';
        return aVal.localeCompare(bVal);
      }
    },
    {
      title: 'Sub Location',
      dataIndex: 'subLocation',
      key: 'subLocation',
      width: 120,
      sorter: (a, b) => {
        const aVal = a.subLocation || '';
        const bVal = b.subLocation || '';
        return aVal.localeCompare(bVal);
      }
    },
    {
      title: 'Previous Full Tank Date',
      dataIndex: 'previousFullTankDate',
      key: 'previousFullTankDate',
      width: 180,
      sorter: (a, b) => {
        const aDate = moment(a.previousFullTankDate, 'YYYY-MM-DD HH:mm:ss');
        const bDate = moment(b.previousFullTankDate, 'YYYY-MM-DD HH:mm:ss');
        if (!aDate.isValid() || !bDate.isValid()) return 0;
        return aDate.valueOf() - bDate.valueOf();
      }
    },
    {
      title: 'Current Full Tank Date',
      dataIndex: 'currentFullTankDate',
      key: 'currentFullTankDate',
      width: 180,
      sorter: (a, b) => {
        const aDate = moment(a.currentFullTankDate, 'YYYY-MM-DD HH:mm:ss');
        const bDate = moment(b.currentFullTankDate, 'YYYY-MM-DD HH:mm:ss');
        if (!aDate.isValid() || !bDate.isValid()) return 0;
        return aDate.valueOf() - bDate.valueOf();
      }
    },
    {
      title: 'Fuel Consumed (Ltrs)',
      dataIndex: 'fuelConsumed',
      key: 'fuelConsumed',
      width: 150,
      align: 'right',
      sorter: (a, b) => {
        const aVal = parseFloat(a.fuelConsumed) || 0;
        const bVal = parseFloat(b.fuelConsumed) || 0;
        return aVal - bVal;
      }
    },
    {
      title: 'Meter Run',
      dataIndex: 'meterRun',
      key: 'meterRun',
      width: 120,
      align: 'right',
      sorter: (a, b) => {
        const aVal = a.meterRun === '-' ? 0 : (parseFloat(a.meterRun) || 0);
        const bVal = b.meterRun === '-' ? 0 : (parseFloat(b.meterRun) || 0);
        return aVal - bVal;
      }
    },
    {
      title: 'Meter Name',
      dataIndex: 'meterName',
      key: 'meterName',
      width: 120,
      sorter: (a, b) => {
        const aVal = a.meterName || '';
        const bVal = b.meterName || '';
        return aVal.localeCompare(bVal);
      }
    },
    {
      title: 'Consumption Rate',
      dataIndex: 'consumptionRate',
      key: 'consumptionRate',
      width: 150,
      align: 'right',
      sorter: (a, b) => {
        const aVal = a.consumptionRate === '-' ? 0 : (parseFloat(a.consumptionRate) || 0);
        const bVal = b.consumptionRate === '-' ? 0 : (parseFloat(b.consumptionRate) || 0);
        return aVal - bVal;
      },
      render: (value, record) => {
        if (value === '-') return '-';
        return `${value} ${record.consumptionRateUnit}`;
      }
    },
    {
      title: 'Standard Rate',
      dataIndex: 'standardRate',
      key: 'standardRate',
      width: 150,
      align: 'right',
      sorter: (a, b) => {
        const aVal = a.standardRate === '-' ? 0 : (parseFloat(a.standardRate) || 0);
        const bVal = b.standardRate === '-' ? 0 : (parseFloat(b.standardRate) || 0);
        return aVal - bVal;
      },
      render: (value, record) => {
        if (value === '-') return '-';
        return `${value} ${record.standardRateUnit}`;
      }
    },
    {
      title: 'Variance',
      dataIndex: 'variance',
      key: 'variance',
      width: 120,
      align: 'right',
      sorter: (a, b) => {
        const aVal = a.variance === '-' ? 0 : (parseFloat(a.variance) || 0);
        const bVal = b.variance === '-' ? 0 : (parseFloat(b.variance) || 0);
        return aVal - bVal;
      },
      render: (value, record) => {
        if (value === '-') return '-';
        const color = parseFloat(value) >= 0 ? 'red' : 'green';
        return <Tag color={color}>{value}</Tag>;
      }
    },
    {
      title: 'Variance %',
      dataIndex: 'variancePercent',
      key: 'variancePercent',
      width: 120,
      align: 'right',
      sorter: (a, b) => {
        const aVal = a.variancePercent === '-' ? 0 : (parseFloat(a.variancePercent) || 0);
        const bVal = b.variancePercent === '-' ? 0 : (parseFloat(b.variancePercent) || 0);
        return aVal - bVal;
      },
      render: (value) => {
        if (value === '-') return '-';
        const numValue = parseFloat(value);
        const color = numValue >= 0 ? 'red' : 'green';
        return <Tag color={color}>{value}%</Tag>;
      }
    },
    {
      title: 'Fuel Loss (Ltrs)',
      dataIndex: 'fuelLoss',
      key: 'fuelLoss',
      width: 130,
      align: 'right',
      sorter: (a, b) => {
        const aVal = a.fuelLoss === '-' ? 0 : (parseFloat(a.fuelLoss) || 0);
        const bVal = b.fuelLoss === '-' ? 0 : (parseFloat(b.fuelLoss) || 0);
        return aVal - bVal;
      },
      render: (value) => {
        if (value === '-') return '-';
        const numValue = parseFloat(value);
        const color = numValue >= 0 ? 'red' : 'green';
        // Show + sign for positive values, negative values already have - sign
        const displayValue = numValue >= 0 ? `+${value}` : value;
        return <Tag color={color}>{displayValue}</Tag>;
      }
    },
    {
      title: 'Voucher No',
      dataIndex: 'voucherNo',
      key: 'voucherNo',
      width: 120,
      sorter: (a, b) => {
        const aVal = a.voucherNo || '';
        const bVal = b.voucherNo || '';
        return aVal.localeCompare(bVal);
      }
    },
    {
      title: 'Action',
      key: 'action',
      width: 100,
      fixed: 'right',
      render: (text, record) => {
        const handleAnalyze = () => {
          // Build URL parameters from row data using the exact event timestamps.
          // FuelAlertAnalysis widens the period internally when calling /fuel/mileage-report
          // so we don't need to pad the URL here.
          const fromDate = record.previousFullTankDate || '';
          const toDate = record.currentFullTankDate || '';

          const params = new URLSearchParams({
            assetCode: record.assetCode || '',
            fromDate: fromDate,
            toDate: toDate,
            voucherNo: record.voucherNo !== '-' ? record.voucherNo : ''
          });
          
          // Open Fuel Alert Analysis in a new window
          const url = `${window.location.origin}${window.location.pathname}#/fuel-alert-analysis?${params.toString()}`;
          window.open(url, '_blank');
        };
        
        return (
          <Button
            type="primary"
            size="small"
            onClick={handleAnalyze}
          >
            <Icon type="search" /> Analyze
          </Button>
        );
      }
    }
  ];

  return (
    <div>
      <Card title="Full Tank to Full Tank Consumption Report" style={{ marginBottom: 16 }}>
        <Row gutter={16}>
          <Col span={24}>
            {/* Date Range and Generate Report Button Row */}
            <div style={{ marginBottom: 16 }}>
              <Row gutter={16} align="bottom">
                <Col span={8}>
                  <div>
                    <label>Date Range:</label>
                    <RangePicker
                      style={{ width: '100%', marginTop: 8 }}
                      format="YYYY-MM-DD"
                      value={dateRange}
                      onChange={setDateRange}
                    />
                  </div>
                </Col>
                <Col span={4}>
                  <Button
                    type="primary"
                    onClick={generateReport}
                    loading={loading}
                    style={{ marginTop: 32 }}
                  >
                    Generate Report
                  </Button>
                </Col>
              </Row>
            </div>
            
            {/* Filters Row - Work reactively on already fetched data - Only show after data is loaded */}
            {allReportData.length > 0 && (
              <>
                <div style={{ marginBottom: 16 }}>
                  <Row gutter={16}>
                    <Col span={6}>
                      <div>
                        <label>Asset Code:</label>
                        <Select
                          style={{ width: '100%', marginTop: 8 }}
                          placeholder="Search by asset code, owner code, or alt name"
                          allowClear
                          showSearch
                          optionFilterProp="children"
                          value={selectedAssetCode}
                          onChange={setSelectedAssetCode}
                          filterOption={(input, option) => {
                            if (!input) return true;

                            const searchText = String(input).toLowerCase().trim();
                            const code = option.props.value;
                            const assetInfo = assetCodeMap[code] || {};
                            const label = String(option.props.children || '').toLowerCase();
                            const ownerCode = String(assetInfo.ownerCode || '').toLowerCase();
                            const altName = String(assetInfo.altName || '').toLowerCase();

                            return label.includes(searchText) ||
                              String(code).toLowerCase().includes(searchText) ||
                              ownerCode.includes(searchText) ||
                              altName.includes(searchText);
                          }}
                        >
                          {assetSearchOptions.map(option => (
                            <Option key={option.value} value={option.value}>
                              {option.label}
                            </Option>
                          ))}
                        </Select>
                      </div>
                    </Col>
                    <Col span={6}>
                      <div>
                        <label>Asset Type:</label>
                        <Select
                          style={{ width: '100%', marginTop: 8 }}
                          placeholder="All"
                          allowClear
                          value={selectedAssetType}
                          onChange={setSelectedAssetType}
                        >
                          {assetTypes.map(type => (
                            <Option key={type} value={type}>{type}</Option>
                          ))}
                        </Select>
                      </div>
                    </Col>
                    <Col span={6}>
                      <div>
                        <label>Asset Sub Type:</label>
                        <Select
                          style={{ width: '100%', marginTop: 8 }}
                          placeholder="All"
                          allowClear
                          value={selectedAssetSubType}
                          onChange={setSelectedAssetSubType}
                        >
                          {assetSubTypes.map(subType => (
                            <Option key={subType} value={subType}>{subType}</Option>
                          ))}
                        </Select>
                      </div>
                    </Col>
                    <Col span={6}>
                      <div>
                        <label>Asset Model:</label>
                        <Select
                          style={{ width: '100%', marginTop: 8 }}
                          placeholder="All"
                          allowClear
                          value={selectedAssetModel}
                          onChange={setSelectedAssetModel}
                        >
                          {assetModels.map(model => (
                            <Option key={model} value={model}>{model}</Option>
                          ))}
                        </Select>
                      </div>
                    </Col>
                  </Row>
                </div>
                <Row gutter={16}>
                  <Col span={6}>
                    <div>
                      <label>Location:</label>
                      <Select
                        style={{ width: '100%', marginTop: 8 }}
                        placeholder="All"
                        allowClear
                        value={selectedLocation}
                        onChange={setSelectedLocation}
                      >
                        {locations.map(loc => (
                          <Option key={loc} value={loc}>{loc}</Option>
                        ))}
                      </Select>
                    </div>
                  </Col>
                  <Col span={6}>
                    <div>
                      <label>Sub Location:</label>
                      <Select
                        style={{ width: '100%', marginTop: 8 }}
                        placeholder="All"
                        allowClear
                        value={selectedSubLocation}
                        onChange={setSelectedSubLocation}
                      >
                        {subLocations.map(subLoc => (
                          <Option key={subLoc} value={subLoc}>{subLoc}</Option>
                        ))}
                      </Select>
                    </div>
                  </Col>
                </Row>
                <Row gutter={16} style={{ marginTop: 16 }}>
                  <Col span={24}>
                    <Checkbox
                      checked={filterWithinThreshold}
                      onChange={(e) => setFilterWithinThreshold(e.target.checked)}
                    >
                      Hide entries within variance threshold (as defined in asset model)
                    </Checkbox>
                  </Col>
                </Row>
              </>
            )}
          </Col>
        </Row>
      </Card>

      <Card>
        {reportData.length > 0 && (
          <div style={{ marginBottom: 16, textAlign: 'right' }}>
            <Button
              type="primary"
              onClick={() => convertTableToCSV(columns, reportData, 'full_tank_mileage_report')}
              disabled={reportData.length === 0}
            >
              <Icon type="download" /> Download CSV
            </Button>
          </div>
        )}
        <Table
          columns={columns}
          dataSource={reportData}
          loading={loading}
          scroll={{ x: 2200 }}
          pagination={{
            pageSize: 50,
            showSizeChanger: true,
            showTotal: (total) => `Total ${total} entries`
          }}
          size="small"
        />
      </Card>
    </div>
  );
}

export default FullTankMileageReport;
